import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["badge", "dropdown", "list"]

  connect() {
    this.isOpen = false
    this.fetchUnreadCount()
    this.pollInterval = setInterval(() => this.fetchUnreadCount(), 30000)
  }

  disconnect() {
    if (this.pollInterval) clearInterval(this.pollInterval)
  }

  toggle() {
    this.isOpen = !this.isOpen
    if (this.isOpen) {
      this.dropdownTarget.classList.remove("hidden")
      this.fetchNotifications()
    } else {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  close() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
  }

  async fetchUnreadCount() {
    try {
      const response = await fetch("/notifications/unread_count", {
        headers: this.authHeaders(),
      })
      if (!response.ok) return

      const data = await response.json()
      const count = data.unread_count
      if (count > 0) {
        this.badgeTarget.textContent = count > 99 ? "99+" : count
        this.badgeTarget.classList.remove("hidden")
      } else {
        this.badgeTarget.classList.add("hidden")
      }
    } catch {
      // Silently ignore
    }
  }

  async fetchNotifications() {
    try {
      const response = await fetch("/notifications.json", {
        headers: this.authHeaders(),
      })
      if (!response.ok) return

      const data = await response.json()
      this.renderNotifications(data.notifications.slice(0, 10))
    } catch {
      // Silently ignore
    }
  }

  async markAsRead(event) {
    const id = event.currentTarget.dataset.notificationId
    try {
      await fetch(`/notifications/${id}/read`, {
        method: "PATCH",
        headers: this.authHeaders(),
      })
      const dot = event.currentTarget.querySelector(".unread-dot")
      if (dot) dot.classList.add("hidden")
      this.fetchUnreadCount()
    } catch {
      // Silently ignore
    }
  }

  async markAllRead() {
    try {
      await fetch("/notifications/read_all", {
        method: "PATCH",
        headers: this.authHeaders(),
      })
      this.listTarget.querySelectorAll(".unread-dot").forEach((dot) => dot.classList.add("hidden"))
      this.fetchUnreadCount()
    } catch {
      // Silently ignore
    }
  }

  renderNotifications(notifications) {
    // Clear existing content
    this.listTarget.replaceChildren()

    if (notifications.length === 0) {
      const emptyDiv = document.createElement("div")
      emptyDiv.className = "px-4 py-6 text-center text-sm text-gray-400"
      emptyDiv.textContent = "No notifications yet"
      this.listTarget.appendChild(emptyDiv)
      return
    }

    notifications.forEach((n) => {
      const button = document.createElement("button")
      button.className =
        "w-full text-left px-4 py-3 hover:bg-gray-700 transition-colors flex items-start gap-3 border-b border-gray-700 last:border-0"
      button.dataset.action = "click->notification-bell#markAsRead"
      button.dataset.notificationId = n.id

      const dot = document.createElement("span")
      dot.className = `unread-dot mt-2 h-2 w-2 rounded-full flex-shrink-0 ${n.read ? "bg-transparent" : "bg-yellow-400"}`
      button.appendChild(dot)

      const content = document.createElement("div")
      content.className = "flex-1 min-w-0"

      const title = document.createElement("p")
      title.className = "text-sm font-medium text-white truncate"
      title.textContent = n.title
      content.appendChild(title)

      if (n.body) {
        const body = document.createElement("p")
        body.className = "text-xs text-gray-400 truncate mt-0.5"
        body.textContent = n.body
        content.appendChild(body)
      }

      const time = document.createElement("p")
      time.className = "text-xs text-gray-500 mt-1"
      time.textContent = this.timeAgo(n.created_at)
      content.appendChild(time)

      button.appendChild(content)
      this.listTarget.appendChild(button)
    })
  }

  timeAgo(isoString) {
    const date = new Date(isoString)
    const now = new Date()
    const seconds = Math.floor((now - date) / 1000)

    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  authHeaders() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return {
      Accept: "application/json",
      "X-CSRF-Token": meta ? meta.content : "",
    }
  }
}
